import React from 'react';
import { Github, Linkedin, Youtube, MessageCircle, Mail, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-black border-t border-orange-500/20 relative">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <h3 className="text-2xl font-bold text-white futuristic-text mb-4">InSkill</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">
                Empowering the next generation of students with cutting-edge AI skills through hands-on learning and mentorship.
              </p>
              
              {/* Social Links */}
              <div className="flex space-x-4">
                {[
                  { icon: Linkedin, href: "#", label: "LinkedIn" },
                  { icon: Github, href: "#", label: "GitHub" },
                  { icon: Youtube, href: "#", label: "YouTube" },
                  { icon: MessageCircle, href: "#", label: "Discord" }
                ].map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 bg-gray-800 hover:bg-orange-500/20 rounded-full flex items-center justify-center transition-all duration-300 group border border-gray-700 hover:border-orange-500/40"
                  >
                    <social.icon className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6 futuristic-text">Quick Links</h4>
              <ul className="space-y-3">
                {[
                  { name: "Home", section: "hero" },
                  { name: "About Us", section: "about" },
                  { name: "Curriculum", section: "courses" },
                  { name: "Student Projects", section: "projects" },
                  { name: "Contact", section: "contact" }
                ].map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.section)}
                      className="text-gray-400 hover:text-orange-500 transition-colors duration-200 text-left"
                    >
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Programs */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6 futuristic-text">Programs</h4>
              <ul className="space-y-3">
                {[
                  "🤖 14-Day AI Bootcamp",
                  "🚀 2-Day AI Mastermind",
                  "Machine Learning Track",
                  "Deep Learning Specialization",
                  "NLP & Generative AI",
                  "Computer Vision",
                  "AI Ethics & Responsibility"
                ].map((program, index) => (
                  <li key={index}>
                    <span className="text-gray-400 hover:text-orange-500 transition-colors duration-200 cursor-pointer">
                      {program}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-lg font-semibold text-white mb-6 futuristic-text">Get in Touch</h4>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-400">hello@inskill.ai</p>
                    <p className="text-gray-500 text-sm">24hr response time</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <MessageCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-gray-400">Join our Discord</p>
                    <p className="text-gray-500 text-sm">24/7 community support</p>
                  </div>
                </div>
              </div>

              {/* Newsletter Signup */}
              <div className="mt-6">
                <h5 className="text-white font-medium mb-3">Stay Updated</h5>
                <div className="flex">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    className="flex-1 bg-gray-800 border border-gray-700 rounded-l-lg px-3 py-2 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none text-sm"
                  />
                  <button className="bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-r-lg transition-colors">
                    <Mail className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <p className="text-gray-400 text-sm">
                © 2024 InSkill. All rights reserved.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  Terms of Service
                </a>
                <a href="#" className="text-gray-400 hover:text-orange-500 text-sm transition-colors">
                  Code of Conduct
                </a>
              </div>
            </div>

            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              className="w-10 h-10 bg-gray-800 hover:bg-orange-500/20 rounded-full flex items-center justify-center transition-all duration-300 border border-gray-700 hover:border-orange-500/40 group"
              aria-label="Back to top"
            >
              <ArrowUp className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
            </button>
          </div>
        </div>

        {/* Additional Info Bar */}
        <div className="border-t border-gray-800 py-4">
          <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-8 text-xs text-gray-500">
            <span>🚀 Next Cohort Starts: March 15, 2024</span>
            <span>📚 Limited to 25 Students</span>
            <span>💡 85% Job Placement Rate</span>
            <span>🌟 4.9/5 Student Rating</span>
          </div>
        </div>
      </div>

      {/* Floating Elements */}
      <div className="absolute bottom-4 left-4 w-32 h-32 bg-orange-500/5 rounded-full blur-2xl"></div>
      <div className="absolute top-4 right-4 w-24 h-24 bg-orange-500/5 rounded-full blur-xl"></div>
    </footer>
  );
};

export default Footer;